import { useMemo } from "react";
import { Flag, Clock, Trophy } from "@phosphor-icons/react";

const fmtHMS = (s) => {
  const sec = Math.max(0, Number(s) || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${sec}s`;
};

/**
 * Detecta si todas las retas de una ronda están en status "finalizado"
 * y muestra un resumen visual con ganadores, puntos repartidos y tiempo total.
 */
export default function RoundClosePanel({ scoreboards, roundNum, testIdPrefix }) {
  const summary = useMemo(() => {
    if (!scoreboards || !scoreboards.length) return null;
    const allFinished = scoreboards.every((s) => s.status === "finalizado");
    if (!allFinished) return null;

    let totalPoints = 0;
    let totalSeconds = 0;
    const winners = []; // by scoreboard
    const playerPts = new Map();

    scoreboards.forEach((s) => {
      const sa = Number(s.score_a) || 0;
      const sb = Number(s.score_b) || 0;
      totalPoints += sa + sb;
      totalSeconds += Number(s.timer_seconds) || 0;
      const lu = s.lineup || {};
      const winSide = sa > sb ? "A" : sb > sa ? "B" : "Empate";
      let winNames = [];
      if (winSide === "A") {
        winNames = [lu.a1?.name, lu.a2?.name].filter(Boolean);
      } else if (winSide === "B") {
        winNames = [lu.b1?.name, lu.b2?.name].filter(Boolean);
      }
      winners.push({ number: s.number, winSide, names: winNames, score: `${sa}-${sb}` });

      [["a1", sa], ["a2", sa], ["b1", sb], ["b2", sb]].forEach(([slot, pts]) => {
        const p = lu[slot];
        if (!p) return;
        const k = p.user_id || p.name;
        if (!k) return;
        const e = playerPts.get(k) || { name: p.name, total: 0 };
        e.total += pts;
        playerPts.set(k, e);
      });
    });

    const topPlayers = Array.from(playerPts.values()).sort((a, b) => b.total - a.total).slice(0, 3);
    return { totalPoints, totalSeconds, winners, topPlayers, count: scoreboards.length };
  }, [scoreboards]);

  if (!summary) return null;

  return (
    <div className="mt-2 p-3 bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-2 border-green-500/30 rounded-md" data-testid={`${testIdPrefix}-round-close-${roundNum}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Flag size={16} weight="fill" className="text-green-400" />
        <span className="text-[11px] tracking-[0.25em] uppercase font-bold text-green-400">Cierre de Ronda {roundNum}</span>
        <span className="ml-auto text-[10px] text-gray-400">{summary.count} retas finalizadas</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <div className="bg-black/30 p-2 rounded border border-white/5">
          <div className="text-[9px] uppercase tracking-wider text-gray-400">Pts repartidos</div>
          <div className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]" data-testid={`${testIdPrefix}-round-close-${roundNum}-points`}>{summary.totalPoints}</div>
        </div>
        <div className="bg-black/30 p-2 rounded border border-white/5">
          <div className="text-[9px] uppercase tracking-wider text-gray-400 flex items-center gap-1"><Clock size={10} /> Tiempo total</div>
          <div className="font-['Barlow_Condensed'] text-2xl font-black text-cyan-400" data-testid={`${testIdPrefix}-round-close-${roundNum}-time`}>{fmtHMS(summary.totalSeconds)}</div>
        </div>
        <div className="bg-black/30 p-2 rounded border border-white/5 col-span-2 sm:col-span-1">
          <div className="text-[9px] uppercase tracking-wider text-gray-400 flex items-center gap-1"><Trophy size={10} /> MVP</div>
          <div className="font-['Barlow_Condensed'] text-lg font-black text-amber-400 truncate" title={summary.topPlayers[0]?.name}>{summary.topPlayers[0]?.name || "—"}</div>
          <div className="text-[10px] text-gray-500">{summary.topPlayers[0]?.total || 0} pts</div>
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400 mb-1">Ganadores de cada reta</div>
        {summary.winners.map((w, i) => (
          <div key={i} className="flex items-center justify-between bg-black/20 px-2 py-1 rounded text-[10px]">
            <span className="text-gray-400">Reta #{w.number}</span>
            <span className="text-white truncate flex-1 mx-2 text-right">
              {w.winSide === "Empate" ? <span className="text-gray-500 italic">Empate</span> : (w.names.join(" + ") || `Pareja ${w.winSide}`)}
            </span>
            <span className="font-mono text-[#CCFF00]">{w.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
